import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Check, Copy, ExternalLink, Mail, ShieldCheck, Trash2, X } from 'lucide-react';
import { createSharedRecordPage, getSharedRecordPages, revokeSharedRecordPage, sendSharedRecordPageEmail, type MyFacility, type SharedRecordPageSummary } from '../lib/api';
import './SharedRecords.css';

const linkDurations = [1, 3, 7, 14];

export default function ShareRecordsDialog({ facility, onClose }: { facility: MyFacility; onClose: () => void }) {
  const { getAccessTokenSilently } = useAuth0();
  const [selectedPetIds, setSelectedPetIds] = useState(() => facility.pets.map((pet) => pet.id));
  const [durationDays, setDurationDays] = useState(7);
  const [recipientEmail, setRecipientEmail] = useState(facility.email || '');
  const [links, setLinks] = useState<SharedRecordPageSummary[]>([]);
  const [createdLink, setCreatedLink] = useState<{ id: string; token: string; url: string; expires_at: string; pets: string[] } | null>(null);
  const [copied, setCopied] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSentTo, setEmailSentTo] = useState<string[]>([]);
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [saving, setSaving] = useState(false);
  const [revokingId, setRevokingId] = useState('');
  const [error, setError] = useState('');
  const [linkError, setLinkError] = useState('');

  useEffect(() => {
    let active = true;
    const loadLinks = async () => {
      setLoadingLinks(true);
      try {
        const token = await getAccessTokenSilently();
        if (!token) throw new Error('Missing access token');
        const result = await getSharedRecordPages(token, facility.id);
        if (active) setLinks(result);
      } catch {
        if (active) setLinkError('Existing links could not be loaded.');
      } finally {
        if (active) setLoadingLinks(false);
      }
    };
    void loadLinks();
    return () => { active = false; };
  }, [facility.id, getAccessTokenSilently]);

  const togglePet = (petId: string) => {
    setSelectedPetIds((current) => current.includes(petId)
      ? current.filter((id) => id !== petId)
      : [...current, petId]);
  };

  const createLink = async () => {
    setSaving(true);
    setError('');
    setCreatedLink(null);
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Missing access token');
      const primaryRecipient = recipientEmail.split(/[;,]/)[0]?.trim() || '';
      const result = await createSharedRecordPage(token, facility.id, selectedPetIds, durationDays, primaryRecipient);
      const { token: shareToken, ...summary } = result;
      setLinks((current) => [summary, ...current.filter((link) => link.id !== result.id)]);
      setCreatedLink({
        id: result.id,
        token: shareToken,
        url: `${window.location.origin}/records/${shareToken}`,
        expires_at: result.expires_at,
        pets: result.pets.map((pet) => pet.name),
      });
      setEmailSentTo([]);
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : 'Unable to create a temporary records link');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    if (!createdLink) return;
    try {
      await navigator.clipboard.writeText(createdLink.url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setError('Unable to copy the link. Select and copy it instead.');
    }
  };

  const sendEmail = async () => {
    if (!createdLink) return;
    const recipients = [...new Set(recipientEmail.split(/[;,]/).map((email) => email.trim().toLowerCase()).filter(Boolean))];
    if (!recipients.length || recipients.some((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) {
      setError('Enter valid email addresses, separated by commas.');
      return;
    }
    setEmailSending(true);
    setError('');
    setEmailSentTo([]);
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Missing access token');
      const result = await sendSharedRecordPageEmail(token, createdLink.id, createdLink.token, recipients);
      setEmailSentTo(result.recipients);
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : 'Unable to send the records link');
    } finally {
      setEmailSending(false);
    }
  };

  const revokeLink = async (pageId: string) => {
    setRevokingId(pageId);
    setLinkError('');
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Missing access token');
      await revokeSharedRecordPage(token, pageId);
      setLinks((current) => current.filter((link) => link.id !== pageId));
      if (createdLink?.id === pageId) setCreatedLink(null);
    } catch (revokeError) {
      setLinkError(revokeError instanceof Error ? revokeError.message : 'Unable to revoke this link');
    } finally {
      setRevokingId('');
    }
  };

  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="modal share-records-modal" role="dialog" aria-modal="true" aria-labelledby="share-records-title">
      <div className="modal-heading">
        <div><p className="eyebrow">Temporary access</p><h2 id="share-records-title">Share records with {facility.name}</h2></div>
        <button className="icon-button" onClick={onClose} aria-label="Close share records"><X size={19} /></button>
      </div>
      <p className="share-records-intro">Choose which linked pets to include and how long the page stays available.</p>
      <fieldset className="share-pet-options">
        <legend>Pets</legend>
        {facility.pets.map((pet) => <label key={pet.id}>
          <input type="checkbox" checked={selectedPetIds.includes(pet.id)} onChange={() => togglePet(pet.id)} />
          <span>{pet.name}</span>
        </label>)}
      </fieldset>
      <div className="share-records-fields">
        <label>Link expires after<select value={durationDays} onChange={(event) => setDurationDays(Number(event.target.value))}>{linkDurations.map((days) => <option key={days} value={days}>{days} {days === 1 ? 'day' : 'days'}</option>)}</select></label>
        <label>Email recipients<input type="text" maxLength={1270} value={recipientEmail} onChange={(event) => setRecipientEmail(event.target.value)} placeholder="care@example.com, owner@example.com" /><small>Separate multiple addresses with commas.</small></label>
      </div>
      <p className="share-records-security"><ShieldCheck size={15} /> You can revoke active links. File links expire after 10 minutes.</p>
      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="modal-actions share-records-create-actions">
        <button className="button button-secondary" onClick={onClose}>Close</button>
        <button className="button button-primary" disabled={saving || selectedPetIds.length === 0} onClick={() => void createLink()}>{saving ? 'Creating...' : 'Create temporary link'}</button>
      </div>
      {createdLink && <section className="share-created-link" aria-label="Created records link">
        <label>Private link<input readOnly value={createdLink.url} onFocus={(event) => event.currentTarget.select()} /></label>
        <div className="share-created-actions">
          <button type="button" className="button button-secondary" onClick={() => void copyLink()}>{copied ? <Check size={15} /> : <Copy size={15} />}{copied ? 'Copied' : 'Copy link'}</button>
          {recipientEmail.trim() && <button type="button" className="button button-secondary" disabled={emailSending} onClick={() => void sendEmail()}><Mail size={15} /> {emailSending ? 'Sending...' : 'Send email'}</button>}
          <a className="button button-secondary" href={createdLink.url} target="_blank" rel="noreferrer"><ExternalLink size={15} /> Preview</a>
        </div>
        {emailSentTo.length > 0 && <p className="share-email-success" role="status">Email accepted for {emailSentTo.join(', ')}.</p>}
      </section>}
      <section className="share-active-links" aria-labelledby="share-active-links-title">
        <h3 id="share-active-links-title">Active links</h3>
        {linkError && <p className="form-error" role="alert">{linkError}</p>}
        {loadingLinks ? <p className="share-links-empty">Loading links...</p> : links.length === 0 ? <p className="share-links-empty">No active links for this facility.</p> : links.map((link) => <article className="share-link-row" key={link.id}>
          <div><strong>{link.pets.map((pet) => pet.name).join(', ')}</strong><span>{link.recipient_email || 'No email address'} · Expires {new Date(link.expires_at).toLocaleDateString()}</span></div>
          <button type="button" className="icon-button danger-icon" disabled={revokingId === link.id} onClick={() => void revokeLink(link.id)} aria-label={`Revoke records link for ${link.pets.map((pet) => pet.name).join(', ')}`} title="Revoke link"><Trash2 size={16} /></button>
        </article>)}
      </section>
    </section>
  </div>;
}